import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { DashboardKpiItem } from "./types";
import {
  dashboardCardHoverShadow,
  dashboardCardShadow,
  dashboardInteractiveCardSx,
} from "../dashboardSurfaceStyles";

interface DashboardKpiCardProps {
  item: DashboardKpiItem;
  onClick?: () => void;
  isLoading?: boolean;
}

export const DashboardKpiCard: React.FC<DashboardKpiCardProps> = ({
  item,
  onClick,
  isLoading = false,
}) => {
  const [showExact, setShowExact] = React.useState(false);

  const accentColor = item.accentColor || "#2563eb";
  const accentBg = item.accentBg || "#eff6ff";

  const isValueToggleable =
    item.id === "total_sales_value" || item.id === "total_service_value";

  const isClickable =
    !!onClick &&
    !isValueToggleable;

  const displayValue =
    isValueToggleable && showExact && item.rawValue !== undefined
      ? new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(item.rawValue))
      : item.value;

  if (isLoading) {
    return (
      <Box
        boxShadow={dashboardCardShadow}
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          minHeight: 96,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="text" width="60%" height={20} />
        </Box>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="text" width="80%" height={15} />
      </Box>
    );
  }

  return (
    <Box sx={{ perspective: "900px" }}>
      <Box
        onClick={isValueToggleable ? () => setShowExact(!showExact) : (isClickable ? onClick : undefined)}
        sx={{
          ...dashboardInteractiveCardSx({ clickable: isClickable || isValueToggleable }),
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          minHeight: 96,
          cursor: isValueToggleable || isClickable ? "pointer" : "default",
          "&:hover": {
            ...dashboardInteractiveCardSx({ clickable: isClickable || isValueToggleable })["&:hover"],
            boxShadow: dashboardCardHoverShadow,
            "& .kpi-card-icon": {
              transform: "translateZ(18px) scale(1.08)",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            transform: "translateZ(12px)",
          }}
        >
          <Box
            className="kpi-card-icon"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              backgroundColor: accentBg,
              color: accentColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: `0 4px 10px ${accentColor}33`,
              transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {item.icon}
          </Box>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "12px",
              fontWeight: 600,
              color: "#111827",
              lineHeight: 1.3,
            }}
          >
            {item.label}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "20px",
            fontWeight: 600,
            color: "#111827",
            lineHeight: 1.1,
            mb: 0.75,
            transform: "translateZ(16px)",
          }}
        >
          {displayValue}
        </Typography>

        {item.helperLabel ? (
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "10px",
              fontWeight: 500,
              color: "#6b7280",
              lineHeight: 1.3,
              mt: "auto",
              transform: "translateZ(8px)",
            }}
          >
            {item.helperLabel}
            {isValueToggleable && (
              <Box
                component="span"
                sx={{
                  color: accentColor,
                  fontWeight: 600,
                  cursor: "pointer",
                  ml: 0.5,
                  display: "inline-block",
                  opacity: 0.85,
                  "&:hover": { textDecoration: "underline", opacity: 1 },
                }}
              >
                ({showExact ? "show compact" : "show exact"})
              </Box>
            )}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};
